import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getClientPortal, getClientOutreach, getClientUpcomingSchedule, type ClientPortalData, type RdvRow, type ClientOutreach, type UpcomingDay } from '../../lib/refonteApi';
import { CONTRACT_LABELS, isBillable, type LifecycleStatus } from '../../lib/appointmentLifecycle';
import { usePortalScope } from './portal/usePortalScope';
import StatusBadge from '../../components/refonte/StatusBadge';
import MethodHint from '../../components/refonte/MethodHint';
import ViewAsClientToggle from '../../components/refonte/ViewAsClientToggle';
import ClientRdvGate from '../../components/refonte/ClientRdvGate';
import { SkeletonRows, Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const dayLabel = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const shortDate = (iso?: string | null) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—');
const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);
const isoDaysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const isoToday = () => new Date().toISOString().slice(0, 10);
const EPOCH = '2020-01-01';

// Vocabulaire hook-brain (definitions.md §1) : booké / honoré / qualifié. Jamais « RDV » tout court.
const HELD_ISH: LifecycleStatus[] = ['held', 'qualified', 'not_qualified', 'validated', 'disputed', 'billed'];
const QUALIFIED_ISH: LifecycleStatus[] = ['qualified', 'validated', 'billed'];
const UPCOMING_ISH: LifecycleStatus[] = ['posed', 'confirmed', 'rescheduled'];

// Période globale : pilote le funnel ET l'activité. 'Tout' = depuis l'origine.
const PRESETS: { label: string; days: number | null }[] = [
  { label: '30 j', days: 30 }, { label: '90 j', days: 90 }, { label: '12 mois', days: 365 }, { label: 'Tout', days: null },
];

const st = (r: RdvRow) => (r.lifecycle_status ?? '') as LifecycleStatus;

// Un maillon du funnel de valeur : grosse valeur + libellé + taux de conversion depuis le maillon précédent.
const FunnelStep: React.FC<{ value: number; label: string; hint?: string; conv?: string; accent?: boolean }> = ({ value, label, hint, conv, accent }) => (
  <div className="flex items-center gap-3">
    <div className={`flex-1 rounded-card border p-4 ${accent ? 'bg-hook-rose/5 border-hook-rose/30' : 'bg-panel border-line'}`}>
      <div className="font-mono text-[9.5px] uppercase tracking-widest text-faint">{label}</div>
      <div className={`mt-1 text-[30px] font-bold font-mono leading-none tabular-nums ${accent ? 'text-hook-rose' : 'text-ink'}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-dim">{hint}</div>}
    </div>
    {conv !== undefined && (
      <div className="flex flex-col items-center text-faint shrink-0 w-9">
        <span className="text-[13px] leading-none">→</span>
        <span className="font-mono text-[10px] mt-1">{conv}</span>
      </div>
    )}
  </div>
);

// Ligne compacte d'un RDV (prochains / derniers honorés).
const RdvLine: React.FC<{ rdv: RdvRow; cQuery: string }> = ({ rdv, cQuery }) => (
  <Link to={`/app/portail/rdv/${rdv.id}${cQuery}`}
    className="flex items-center gap-3 py-2 px-1 -mx-1 rounded-lg hover:bg-panel-2 group">
    <div className="font-mono text-[10px] text-dim w-14 shrink-0 tabular-nums">{shortDate(rdv.date)}</div>
    <div className="min-w-0 flex-1">
      <div className="text-[12.5px] font-semibold truncate">
        {rdv.prospect_name || 'Prospect'}
        {rdv.company_name && <span className="text-dim font-normal"> · {rdv.company_name}</span>}
      </div>
      {rdv.job_title && <div className="text-[10.5px] text-faint truncate">{rdv.job_title}</div>}
    </div>
    <StatusBadge status={rdv.lifecycle_status ?? null} />
    <span className="font-mono text-[11px] text-faint group-hover:text-ink">›</span>
  </Link>
);

// Portail client — TABLEAU DE BORD. Funnel de valeur (booké→honoré→qualifié) filtrable par période,
// prochains RDV, derniers honorés, activité par canal (Mail/LinkedIn « à venir »), résumés de journée.
const ClientPortalPage: React.FC = () => {
  const scope = usePortalScope();
  const { clientId, clientName } = scope;

  const [from, setFrom] = useState(isoDaysAgo(365)); // défaut : 12 mois (évite les trous d'activité récente)
  const [to, setTo] = useState(isoToday());
  const [data, setData] = useState<ClientPortalData | null>(null);
  const [outreach, setOutreach] = useState<ClientOutreach | null>(null);
  const [nextDays, setNextDays] = useState<UpcomingDay[]>([]); // prochaines journées planifiées (retour V1)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [gateOff, setGateOff] = useState(false); // « remplir plus tard » ferme la pop-up pour la session

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const [d, o, nd] = await Promise.all([
        getClientPortal(clientId, from, to),
        getClientOutreach(clientId, from + 'T00:00:00', to + 'T23:59:59').catch(() => null),
        getClientUpcomingSchedule(clientId).catch(() => []),
      ]);
      setData(d); setOutreach(o); setNextDays(nd);
    }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [clientId, from, to]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (days: number | null) => { setFrom(days == null ? EPOCH : isoDaysAgo(days)); setTo(isoToday()); };
  const activePreset = (days: number | null) => (days == null ? from === EPOCH : from === isoDaysAgo(days)) && to === isoToday();
  const k = data?.kpis;
  const cQuery = clientId ? `?c=${clientId}${scope.viewAsClient ? '&asclient=1' : ''}` : (scope.viewAsClient ? '?asclient=1' : '');
  const today = isoToday();

  const rdv = data?.rdv ?? [];
  // Funnel EXACT côté serveur (bornes from/to appliquées, « No reply » exclus, pas de troncature à 200).
  const fBooked = data?.funnel.booked ?? 0;
  const fHeld = data?.funnel.held ?? 0;
  const fQualified = data?.funnel.qualified ?? 0;
  const fBillable = data?.funnel.billable ?? 0;
  const fNoShow = data?.funnel.noShow ?? 0;
  const fCancelled = data?.funnel.cancelled ?? 0;
  // « Bookés » = tout l'effort fourni (total conservé) ; le détail annulés / à venir fait retomber
  // le compte juste pour le client (held + no-show + annulés + à venir = bookés). Réunion 15/10.
  const fPending = Math.max(0, fBooked - fHeld - fNoShow - fCancelled);
  const bookedHint = (() => {
    const parts = [
      fCancelled > 0 ? `${fCancelled} annulé${fCancelled > 1 ? 's' : ''}` : null,
      fPending > 0 ? `${fPending} à venir` : null,
    ].filter(Boolean);
    return parts.length ? `dont ${parts.join(' · ')}` : 'créneaux posés avec un prospect';
  })();

  // Prochains RDV (à venir) et derniers honorés — indépendants de la période.
  const upcoming = rdv
    .filter(r => UPCOMING_ISH.includes(st(r)) && r.date && r.date >= today)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
    .slice(0, 6);
  const recentHeld = rdv.filter(r => HELD_ISH.includes(st(r))).slice(0, 6);
  const toConfirm = rdv.filter(r => ['posed', 'confirmed'].includes(r.lifecycle_status ?? '') && (!r.date || r.date <= today));
  // File de la pop-up obligatoire : RDV passés non résolus (présence et/ou qualif manquante).
  // Date-plancher 30 j : ne pas dérouler le backlog historique au premier login (Mavala : 103 RDV !)
  // ni demander de qualifier hors du délai de forclusion (7 j ouvrés) — audit 08/10. Les « No reply »
  // hérités de la V1 sont des bookés fantômes : exclus.
  const gateFloor = isoDaysAgo(30);
  const gateQueue = rdv
    .filter(r => !!r.date && r.date <= today && r.date >= gateFloor
      && ['posed', 'confirmed', 'held'].includes(r.lifecycle_status ?? '')
      && !['No reply – VM/SMS', 'no_reply'].includes((r as any).status ?? ''))
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    .slice(0, 15);

  return (
    <div>
      {!loading && !gateOff && clientId && gateQueue.length > 0 && (
        <ClientRdvGate queue={gateQueue} onClose={() => setGateOff(true)} onChanged={load} />
      )}
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Tableau de bord</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {scope.isInternal && <ViewAsClientToggle on={scope.viewAsClient} onChange={scope.setViewAsClient} />}
          {(scope.isInternal || scope.options.length > 1) && (
            <label className="flex items-center gap-2 text-[12px] text-dim">
              {scope.isInternal && !scope.viewAsClient && <span className="font-mono text-[9px] uppercase tracking-widest text-faint">QA · client</span>}
              <select value={clientId ?? ''} onChange={e => scope.setClientId(e.target.value || null)}
                className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[240px]">
                {!clientId && <option value="">— choisir —</option>}
                {scope.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">
        Vos rendez-vous et l'activité de prospection · lecture seule
        {clientName && <span className="text-faint"> · {clientName}</span>}
        {data?.contractType && <span className="text-faint"> · contrat {CONTRACT_LABELS[data.contractType]}</span>}
      </p>

      {(err || scope.error) && <EmptyState tone="error" title="Impossible de charger votre espace" hint={err || scope.error || ''} className="mb-4" />}

      {scope.resolving ? (
        <Skeleton className="h-[92px] rounded-card mb-4" />
      ) : !clientId ? (
        <EmptyState title="Aucun client rattaché" hint="Votre compte n'est associé à aucun client pour l'instant." />
      ) : (
        <>
          {/* Période globale — pilote le funnel ET l'activité */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="font-mono text-[9px] uppercase tracking-widest text-faint mr-1">Période</span>
            {PRESETS.map(p => (
              <button key={p.label} onClick={() => applyPreset(p.days)}
                className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${activePreset(p.days) ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{p.label}</button>
            ))}
            <span className="w-px h-5 bg-line-2 mx-1" />
            <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[12px]" />
            <span className="text-faint text-[12px]">→</span>
            <input type="date" value={to} min={from} max={isoToday()} onChange={e => setTo(e.target.value)} className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[12px]" />
          </div>

          {/* Rappel : RDV passés à confirmer (« eu lieu ? ») — action attendue */}
          {toConfirm.length > 0 && (
            <div className="bg-hook-rose/5 border border-hook-rose/30 rounded-card p-3 mb-4 flex items-center gap-3 flex-wrap">
              <span className="text-[13px]"><b>{toConfirm.length} rendez-vous passé(s)</b> à confirmer — ouvrez la fiche pour indiquer s'ils ont eu lieu.</span>
              <Link to={`/app/portail/registre${cQuery}`} className="ml-auto text-[12px] font-semibold text-hook-rose hover:underline">Voir le registre →</Link>
            </div>
          )}

          {/* FUNNEL DE VALEUR — booké → honoré → qualifié (période sélectionnée) */}
          <div className="bg-panel border border-line rounded-card p-5 mb-4">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim">Vos rendez-vous · du booké au qualifié</h2>
              <Link to={`/app/portail/registre${cQuery}`} className="text-[12px] font-semibold text-hook-rose hover:underline">Registre complet →</Link>
            </div>
            {loading ? <SkeletonRows count={2} /> : fBooked === 0 ? (
              <EmptyState title="Aucun rendez-vous sur la période" hint="Élargis la période ci-dessus (essaie « Tout »)." />
            ) : (
              <div className={`grid grid-cols-1 gap-2 ${data?.billsPerRdv ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                <FunnelStep value={fBooked} label="Bookés" hint={bookedHint} conv={`${pct(fHeld, fBooked - fCancelled)}%`} />
                {/* Qualif = hybride only (cerveau §1) : hors hybride elle n'est pas mesurée → ne pas afficher un « 0 » trompeur au client. */}
                <FunnelStep value={fHeld} label="Honorés" hint={fNoShow > 0 ? `${fNoShow} non honoré${fNoShow > 1 ? 's' : ''}` : 'tous présents'} conv={data?.billsPerRdv ? `${pct(fQualified, fHeld)}%` : undefined} />
                {/* Qualifiés = la vraie 3e marche (l'ancienne tuile n'affichait que validés/facturés → faux zéro pour 3 hybrides sur 4, audit 08/10) */}
                {data?.billsPerRdv && <FunnelStep value={fQualified} label="Qualifiés" hint={`honorés ET conformes à vos critères de qualification · dont ${fBillable} validé${fBillable > 1 ? 's' : ''} à facturer`} accent />}
              </div>
            )}
          </div>

          {/* Prochaines journées de prospection planifiées — « quand appelez-vous pour moi ? »
              (la V1 l'affichait, la V2 l'avait perdu : régression n°1 côté client, audit 08/10) */}
          {nextDays.length > 0 && (
            <div className="bg-panel border border-line rounded-card p-5 mb-4">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3">Prochaines journées de prospection planifiées</h2>
              <div className="flex flex-wrap gap-2">
                {nextDays.map(d => (
                  <span key={d.date} className="inline-flex items-center gap-1.5 text-[12.5px] border border-line rounded-lg px-3 py-1.5 bg-panel-2">
                    <span className="font-semibold">{new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    {d.day_type && d.day_type !== 'prospection' && <span className="text-faint font-mono text-[10px]">{d.day_type}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Prochains RDV + derniers honorés */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="bg-panel border border-line rounded-card p-5">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3">Prochains rendez-vous</h2>
              {loading ? <SkeletonRows count={3} /> : upcoming.length === 0 ? (
                <EmptyState title="Aucun rendez-vous à venir" hint="Les nouveaux créneaux apparaîtront ici dès qu'ils seront posés." />
              ) : (
                <div className="divide-y divide-line">{upcoming.map(r => <RdvLine key={r.id} rdv={r} cQuery={cQuery} />)}</div>
              )}
            </div>
            <div className="bg-panel border border-line rounded-card p-5">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3">Derniers rendez-vous honorés</h2>
              {loading ? <SkeletonRows count={3} /> : recentHeld.length === 0 ? (
                <EmptyState title="Aucun rendez-vous honoré" hint="Ils s'afficheront ici une fois tenus." />
              ) : (
                <div className="divide-y divide-line">{recentHeld.map(r => <RdvLine key={r.id} rdv={r} cQuery={cQuery} />)}</div>
              )}
            </div>
          </div>

          {/* Activité de prospection par canal (mail / LinkedIn à venir) */}
          <div className="bg-panel border border-line rounded-card p-5 mb-4">
            <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-4">Activité de prospection · période sélectionnée</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Canal appel — actif */}
              <div className="rounded-card border border-line bg-panel-2 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-semibold">📞 Conversations</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-st-ok">actif</span>
                </div>
                {loading ? <SkeletonRows count={2} /> : (
                  <div className="space-y-1 font-mono text-[11px] text-dim">
                    {/* Dico §2 : connecté = décroché (>0s) ; conversation = >30s ; conv→booké se calcule sur les conversations.
                        « Composés » (volume brut Minari) non affiché : mesurable seulement par SDR/jour, pas par client → chantier calls P2.
                        « Appels passés » retiré : call_logs ne contient pas les tentatives, le chiffre sous-estimait gravement. */}
                    <div className="flex justify-between"><span>Connectés<MethodHint text="Appels décrochés par un humain — mesuré par la téléphonie, consolidé en fin de journée." /></span><b className="text-ink">{k?.connected30 ?? 0}</b></div>
                    <div className="flex justify-between"><span>Conversations<MethodHint text="Appels décrochés de plus de 30 secondes — l'unité de travail qui compte chez Hook." /></span><b className="text-ink">{k?.conversations30 ?? 0}</b></div>
                    <div className="flex justify-between"><span>Bookés pris</span><b className="text-ink">{k?.rdv30 ?? 0}</b></div>
                    <div className="flex justify-between"><span>Conv. → booké</span><b className="text-ink">{pct(k?.rdv30 ?? 0, k?.conversations30 ?? 0)}%</b></div>
                  </div>
                )}
              </div>
              {/* Canal mail (Instantly) — réel si le client a des campagnes/réponses, sinon « à venir ». */}
              {([['mail', '✉️ Mail', outreach?.mail], ['linkedin', 'in LinkedIn', outreach?.linkedin]] as const).map(([key, label, s]) => {
                const has = !!s && (s.sent > 0 || s.campaigns > 0 || s.replies > 0);
                return (
                  <div key={key} className={`rounded-card border p-4 ${has ? 'border-line bg-panel-2' : 'border-dashed border-line-2 bg-transparent opacity-70'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-[12px] font-semibold ${has ? '' : 'text-dim'}`}>{label}</span>
                      <span className={`font-mono text-[9px] uppercase tracking-widest ${has ? 'text-st-ok' : 'text-faint'}`}>{has ? 'actif' : 'à venir'}</span>
                    </div>
                    {loading ? <SkeletonRows count={2} /> : has ? (
                      <div className="space-y-1 font-mono text-[11px] text-dim">
                        <div className="flex justify-between"><span>{key === 'mail' ? 'Mails envoyés' : 'Messages envoyés'}<MethodHint text={key === 'mail' ? 'Nombre de mails envoyés sur la période — mesuré directement depuis Instantly.' : 'Messages LinkedIn envoyés sur la période — mesuré directement depuis HeyReach (hors invitations de connexion).'} /></span><b className="text-ink">{s!.sent}</b></div>
                        <div className="flex justify-between"><span>Réponses<MethodHint text="Réponses reçues des prospects sur ce canal." /></span><b className="text-ink">{s!.replies}</b></div>
                        <div className="flex justify-between"><span>Réponses positives<MethodHint text="Réponses marquées positives (intérêt, demande de RDV) — qualifiées automatiquement puis vérifiées." /></span><b className="text-hook-rose">{s!.positives}</b></div>
                        <div className="flex justify-between"><span>Taux de réponse</span><b className="text-ink">{pct(s!.replies, s!.sent)}%</b></div>
                      </div>
                    ) : <p className="text-[11px] text-faint leading-snug">{key === 'mail' ? 'Campagnes et réponses arriveront ici.' : 'Invitations, acceptations et messages arriveront ici.'}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mail & LinkedIn : résumé léger → détail dans le registre des campagnes */}
          {outreach?.hasAny && (
            <Link to={`/app/portail/campagnes${cQuery}`} className="flex items-center gap-3 bg-panel border border-line rounded-card p-4 mb-4 hover:border-hook-rose transition-colors">
              <span className="text-[13px]">💬</span>
              <div className="text-[13px]">
                <b>{outreach.mail.positives + outreach.linkedin.positives}</b> réponses positives · {outreach.mail.replies + outreach.linkedin.replies} réponses mail &amp; LinkedIn
              </div>
              <span className="ml-auto text-[12px] font-semibold text-hook-rose">Registre des campagnes →</span>
            </Link>
          )}

          {/* Résumés de journée */}
          <div className="bg-panel border border-line rounded-card p-5">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim">Résumés de journée · période sélectionnée</h2>
              <Link to={`/app/portail/journees${cQuery}`} className="text-[12px] font-semibold text-hook-rose hover:underline">Toutes les journées →</Link>
            </div>
            {loading ? <SkeletonRows count={5} /> : (data?.days.length ?? 0) === 0 ? (
              <EmptyState title="Aucune activité sur la période" hint="Élargis la période ou choisis d'autres dates." />
            ) : (
              <div className="space-y-2 max-h-[62vh] overflow-y-auto -mx-1 px-1">
                {data!.days.map(d => (
                  <details key={d.date} className="border border-line rounded-lg group">
                    <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center gap-3 hover:bg-panel-2 rounded-lg">
                      <span className="text-[12.5px] font-semibold capitalize flex-1 min-w-0 truncate">{dayLabel(d.date)}</span>
                      {d.connected > 0 && <span className="font-mono text-[10px] text-dim">{d.connected} connectés</span>}
                      {d.conversations > 0 && <span className="font-mono text-[10px] text-dim">{d.conversations} conversations</span>}
                      {!!d.mailSent && <span className="font-mono text-[10px] text-dim">✉️ {d.mailSent}</span>}
                      {!!d.liSent && <span className="font-mono text-[10px] text-dim">💼 {d.liSent}</span>}
                      {d.rdv > 0 && <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-hook-rose/10 text-hook-rose">{d.rdv} booké{d.rdv > 1 ? 's' : ''}</span>}
                      <Link to={`/app/portail/journees${cQuery ? cQuery + '&' : '?'}d=${d.date}`} onClick={e => e.stopPropagation()} className="font-mono text-[10px] text-hook-rose hover:underline">détail →</Link>
                      <span className="font-mono text-[10px] text-faint group-open:rotate-90 transition-transform">›</span>
                    </summary>
                    <div className="px-3 pb-3 pt-1 space-y-2 border-t border-line">
                      {d.items.length === 0 ? (
                        <p className="text-[11.5px] text-faint pt-2">Pas de résumé de conversation disponible pour ce jour.</p>
                      ) : d.items.map((it, i) => (
                        <div key={i} className="text-[12px] pt-2">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-semibold truncate">{it.company || 'Contact'}</span>
                            {it.contact && <span className="text-faint text-[11px] truncate">· {it.contact}</span>}
                          </div>
                          <p className="text-dim leading-snug">{it.summary}</p>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>

          <p className="font-mono text-[10px] text-faint mt-4">
            Données réelles. Funnel et activité sur la période choisie. Connectés = appels décrochés ; conversations = discussions de plus de 30 s. Résumés issus de la transcription des conversations.
          </p>
        </>
      )}
    </div>
  );
};

export default ClientPortalPage;
